import moment from 'moment'
import { getShortMonth, padZero } from 'shared/libraries/utils/date'
import { MONTHS_LIST } from 'shared/constants'

const getYears = startYear => {
  let currentYear = parseInt(moment().format('YYYY'), 10)
  let years = []
  for (let year = startYear; year <= currentYear; year++) {
    years.push(year)
  }
  return years
}

const getChartOptions = () => {
  const years = getYears(2019)
  const yearOptions = [
    { label: 'All', value: 'all' },
    ...years.map(year => ({ label: year, value: year })),
  ]
  const monthOptions = MONTHS_LIST.map((month, index) => ({
    value: padZero(index + 1),
    label: month,
    short: getShortMonth(index),
  }))
  return { yearOptions, monthOptions }
}

const getCategories = chart => {
  const { monthOptions, yearOptions } = getChartOptions()
  let categories = []
  let sortedKeyFormat = 'YYYY'
  if (chart.type === 'year') {
    if (chart.years.includes('all')) {
      categories = yearOptions
        .filter(year => year.value !== 'all')
        .map(year => year.value)
    } else if (chart.years.length === 1) {
      categories = monthOptions.map(month => month.short)
      sortedKeyFormat = 'MMM'
    } else if (chart.years.length > 1) {
      categories = chart.years
    }
  } else if (chart.type === 'month') {
    if (chart.years.includes('all')) {
      if (chart.months.length === 0) {
        categories = yearOptions
          .filter(year => year.value !== 'all')
          .map(year => year.value)
      } else {
        categories = yearOptions
          .filter(year => year.value !== 'all')
          .reduce((prev, year) => {
            return prev.concat(
              chart.months.map(month => `${year.value}-${month}`),
            )
          }, [])
        sortedKeyFormat = 'YYYY-MM'
      }
    } else if (chart.months.length === 1 && chart.years.length === 1) {
      categories = Array.from(
        {
          length: moment(`${chart.years[0]}-${chart.months[0]}`).daysInMonth(),
        },
        (_, i) =>
          moment()
            .date(i + 1)
            .format('D'),
      )
      sortedKeyFormat = 'D'
    } else if (chart.months.length >= 1) {
      categories = chart.years.reduce((prev, year) => {
        return prev.concat(chart.months.map(month => `${year}-${month}`))
      }, [])
      sortedKeyFormat = 'YYYY-MM'
    } else {
      if (chart.years.length === 1) {
        categories = monthOptions.map(month => month.short)
        sortedKeyFormat = 'MM'
      } else if (chart.years.length > 1) {
        categories = chart.years
      }
    }
  }
  return {
    categories: categories.sort((a, b) => new Date(a) - new Date(b)),
    sortedKeyFormat,
  }
}

const getSelectedMonths = ({ chart, monthOptions, yearOptions }) =>
  yearOptions.reduce((months, { value: year }) => {
    if (year === 'all') return months
    if (chart.years.includes('all')) {
      if (chart.type === 'year') {
        months = months.concat(
          monthOptions.map(({ value: month }) => `${year}-${month}`),
        )
      } else if (chart.type === 'month') {
        months = months.concat(chart.months.map(month => `${year}-${month}`))
      }
    } else {
      if (chart.years.includes(year)) {
        if (chart.type === 'year') {
          months = months.concat(
            monthOptions.map(({ value: month }) => `${year}-${month}`),
          )
        } else if (chart.type === 'month') {
          months = months.concat(chart.months.map(month => `${year}-${month}`))
        }
      }
    }

    return months
  }, [])

const getSeries = ({
  recruitmentChannelsLog,
  brandAmbassadors,
  chart,
  sortedKeyFormat,
  categories,
}) => {
  const { monthOptions, yearOptions } = getChartOptions()
  const filteredMonths = getSelectedMonths({
    chart,
    monthOptions,
    yearOptions,
  })

  let channelsList = {}
  filteredMonths.map(month => {
    const channels = recruitmentChannelsLog
      .sort((a, b) => b.createdAt - a.createdAt)
      .find(
        log =>
          moment
            .unix(log.createdAt / 1000)
            .isBefore(moment(month, 'YYYY-MM')) ||
          moment.unix(log.createdAt / 1000).isSame(moment(month, 'YYYY-MM')),
      )
    channelsList[month] = channels ? channels.selectedChannels : []
    return null
  })

  const uniqChannels = [
    ...new Set([
      ...Object.values(channelsList).reduce((acc, channels) => {
        return acc.concat(channels)
      }, []),
      'Other',
    ]),
  ].flat()

  const { sortedData } = brandAmbassadors.reduce(
    ({ sortedData }, { recruitmentChannel, recruitmentChannelSelectedAt }) => {
      const key = moment
        .unix(recruitmentChannelSelectedAt / 1000)
        .utc()
        .format(sortedKeyFormat)

      if (
        recruitmentChannel &&
        filteredMonths.includes(
          moment
            .unix(recruitmentChannelSelectedAt / 1000)
            .utc()
            .format('YYYY-MM'),
        )
      ) {
        if (uniqChannels.indexOf(recruitmentChannel) === -1) {
          recruitmentChannel = 'Other'
        }

        if (sortedData[key]) {
          sortedData[key] = {
            ...sortedData[key],
            [recruitmentChannel]: sortedData[key][recruitmentChannel]
              ? sortedData[key][recruitmentChannel] + 1
              : 1,
          }
        } else {
          sortedData[key] = {
            [recruitmentChannel]: 1,
          }
        }
      }

      return { sortedData }
    },
    { sortedData: Object.fromEntries(categories.map(key => [key, {}])) },
  )

  return uniqChannels
    .sort((a, b) =>
      a.toLocaleLowerCase() < b.toLocaleLowerCase()
        ? -1
        : a.toLocaleLowerCase() > b.toLocaleLowerCase()
        ? 1
        : 0,
    )
    .map(name => ({
      name: name.length > 20 ? `${name.slice(0, 20)}...` : name,
      data: Object.keys(sortedData).map(key =>
        sortedData[key][name] ? sortedData[key][name] : 0,
      ),
    }))
}

export { getCategories, getSeries, getChartOptions }
